import { Validator } from 'lakmus'
import { repository } from 'redux-scaffolding-ts'
import { DataStore, Query, QueryResult } from 'src/core/stores/dataStore'
import { FormStore } from 'src/core/stores/formStore'
import { container } from 'src/inversify.config'
import HttpService from '../core/services/http.service'
import { CommandResult } from '../core/stores/types'

export interface NewRestrictionPlanItem {
    displayName: string
    companyId: string
    // weekly and daily hours restrictions
    mondayMinHours?: number
    mondayMaxHours?: number
    tuesdayMinHours?: number
    tuesdayMaxHours?: number
    wednesdayMinHours?: number
    wednesdayMaxHours?: number
    thursdayMinHours?: number
    thursdayMaxHours?: number
    fridayMinHours?: number
    fridayMaxHours?: number
    saturdayMinHours?: number
    saturdayMaxHours?: number
    sunMinHours?: number
    sunMaxHours?: number
    weekMinHours?: number
    weekMaxHours?: number
}

export interface RestrictionPlanItem extends NewRestrictionPlanItem {
    id: string
}

export interface RestrictionPlanSummary {
    id: string
    displayName: string
    companyId: string
    weekMinHours?: number
    weekMaxHours?: number
}

export class NewRestrictionPlanValidator extends Validator<NewRestrictionPlanItem> {
    constructor() {
        super()
        this.ruleFor(x => x.displayName)
            .notNull()
            .withMessage('Plan must have a name.')
    }
}

@repository('@@RESTRICTION_PLANS', 'restrictionplans.summary')
export class RestrictionPlansStore extends DataStore<RestrictionPlanSummary> {
    baseUrl: string = ``

    constructor() {
        super(
            'RESTRICTION_PLAN',
            {
                count: 0,
                isBusy: false,
                items: [],
                result: undefined,
                discard: item => { }
            },
            container
        )
    }

    public changeUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/restrictionplans`
    }

    public async getAllAsync(
        query: Query,
        data?: any
    ): Promise<QueryResult<RestrictionPlanSummary>> {
        let httpService = this.container.get<HttpService>(HttpService)
        const result = await this.dispatchAsync(
            this.ENTITY_LIST_UPDATE as string,
            httpService.get<QueryResult<RestrictionPlanSummary>>(
                `${this.baseUrl}?${DataStore.buildUrl(query)}`,
                data
            )
        )
        return result.data
    }
}

const RESTRICTION_PLAN_UPDATE_ITEM = 'RESTRICTION_PLAN_UPDATE_ITEM'
@repository('@@RESTRICTION_PLANS', 'restrictionPlans.detail')
export class RestrictionPlanStore extends FormStore<RestrictionPlanItem> {
    baseUrl: string = ``

    protected validate(item: RestrictionPlanItem) {
        return new NewRestrictionPlanValidator().validate(item)
    }

    constructor() {
        super(
            'RESTRICTION_PLAN',
            {
                isBusy: false,
                status: 'New',
                item: undefined,
                result: undefined
            },
            container
        )
        this.addReducer(
            RESTRICTION_PLAN_UPDATE_ITEM,
            (): any => {
                return super.onPatch()
            },
            'AsyncAction'
        )
    }

    public changeUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/restrictionplans`
    }

    public async getById(
        id: string,
        params?: any,
        companyId?: string
    ): Promise<RestrictionPlanItem> {
        var httpService = this.container.get<HttpService>(HttpService)
        const result = await this.dispatchAsync(
            this.ENTITY_LOAD,
            httpService.get<RestrictionPlanItem>(`${this.baseUrl}/${id}`, params)
        )
        return result.data
    }

    public async update(item: RestrictionPlanItem) {
        var result = (await super.patch(RESTRICTION_PLAN_UPDATE_ITEM, `${item.id}`, item)) as any
        return result.data as CommandResult<RestrictionPlanItem>
    }
}

@repository('@@RESTRICTON_PLANS', 'restrictionPlans.new')
export class NewRestrictionPlanStore extends FormStore<NewRestrictionPlanItem> {
    baseUrl: string = ``

    protected validate(item: NewRestrictionPlanItem) {
        return new NewRestrictionPlanValidator().validate(item)
    }

    constructor() {
        super(
            'NEW_RESTRICTION_PLAN',
            {
                isBusy: false,
                status: 'New',
                item: undefined,
                result: undefined
            },
            container
        )
    }

    public changeUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/restrictionplans`
    }
}